'use client';

import React from 'react';
import { Award, CheckCircle2, Eye, Link2, Lock, Medal, ShieldCheck } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';

const LIST_ITEM_KEYS = [
  'landing_dual_ledger.item1',
  'landing_dual_ledger.item2',
  'landing_dual_ledger.item3',
  'landing_dual_ledger.item4',
] as const;

const FEATURE_KEYS = [
  { icon: Lock, titleKey: 'landing_dual_ledger.feature1_title', descKey: 'landing_dual_ledger.feature1_desc' },
  { icon: Eye, titleKey: 'landing_dual_ledger.feature2_title', descKey: 'landing_dual_ledger.feature2_desc' },
  { icon: Medal, titleKey: 'landing_dual_ledger.feature3_title', descKey: 'landing_dual_ledger.feature3_desc' },
] as const;

const COLOR_LABEL_KEYS = [
  { color: '#C9A84C', labelKey: 'landing_dual_ledger.card_color1', fallback: 'Gold' },
  { color: '#EC4899', labelKey: 'landing_dual_ledger.card_color2', fallback: 'Story' },
  { color: '#06B6D4', labelKey: 'landing_dual_ledger.card_color3', fallback: 'Proof' },
] as const;

const CARD_COPY = {
  publicRecord: 'Public Verification Record',
  authenticated: 'Authenticated',
  cardTitle: 'Public Proof Card',
  cardSubtitle: 'Verified record summary with evidence and review context',
  pillarLabel: 'Pillar',
  degreeTitle: 'BSc Computer Science',
  degreeDetail: 'Degree record with institution review and supporting evidence',
  badge1: 'Institution reviewed',
  badge2: 'Evidence attached',
  badge3: 'Public proof ready',
  individualLabel: 'Individual Vault',
  individualTitle: 'Personal achievement record',
  individualBody: 'Stored with owner-controlled visibility and a public verification link.',
  pillarColLabel: 'Record pillar',
  pillarColValue: 'Achievement',
  verifiedLabel: 'Verified Record',
  verifiedTitle: 'Review trail attached',
  verifiedBody: 'Verification ID and evidence summary available for public review.',
  copyUrl: 'ertzyx.com/verify/ERV-2024-0891-A4F2',
  copyButton: 'Copy',
} as const;

export default function DualLedgerSection() {
  const { safeT } = useTranslation();

  return (
    <section className="overflow-x-hidden bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-10 2xl:px-16">
        <div className="grid grid-cols-1 items-center gap-12 xl:grid-cols-2 xl:gap-16">
          <div className="relative w-full min-w-0 max-w-full">
            <div
              className="absolute inset-0 rounded-3xl opacity-40 blur-2xl"
              style={{
                background:
                  'radial-gradient(circle at 20% 20%, rgba(201,168,76,0.34), transparent 36%), radial-gradient(circle at 80% 24%, rgba(236,72,153,0.24), transparent 34%), radial-gradient(circle at 72% 82%, rgba(34,211,238,0.24), transparent 36%)',
              }}
            />

            <div
              className="relative mx-auto w-full max-w-full overflow-hidden rounded-3xl border-2 border-archive-gold/30 bg-white shadow-2xl"
              style={{ width: 'min(100%, calc(100vw - 32px))' }}
            >
              <div className="h-2 w-full bg-gradient-to-r from-archive-gold via-rose-400 via-violet-400 to-cyan-400" />

              <div className="w-full min-w-0 p-5 sm:p-8">
                <div className="mb-6 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-trust-blue">
                      <Lock size={18} className="text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-trust-blue">Ertzyx</p>
                      <p className="break-words text-xs text-[#001E5A]/70">
                        {safeT('landing_dual_ledger.public_record', CARD_COPY.publicRecord)}
                      </p>
                    </div>
                  </div>
                  <div className="flex w-fit max-w-full shrink-0 items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1.5">
                    <div className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-green-500" />
                    <span className="whitespace-normal break-words text-xs font-semibold text-green-700">
                      {safeT('landing_dual_ledger.authenticated', CARD_COPY.authenticated)}
                    </span>
                  </div>
                </div>

                <div className="mb-6 w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-archive-gold/30 bg-gradient-to-br from-archive-gold-pale via-white to-rose-50 p-4 sm:p-5">
                  <div className="mb-4 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-xs font-bold uppercase tracking-[0.18em] text-amber-700">
                        {safeT('landing_dual_ledger.card_title', CARD_COPY.cardTitle)}
                      </p>
                      <p className="mt-1 break-words text-xs font-semibold leading-relaxed text-[#001E5A]/75">
                        {safeT('landing_dual_ledger.card_subtitle', CARD_COPY.cardSubtitle)}
                      </p>
                    </div>
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-2xl bg-gradient-to-br from-archive-gold via-rose-400 to-violet-500 text-white shadow-lg sm:h-12 sm:w-12">
                      <Award size={22} />
                    </div>
                  </div>
                  <p className="mb-1 break-words text-xs font-semibold uppercase tracking-wider text-[#001E5A]/60">
                    {safeT('landing_dual_ledger.card_pillar_label', CARD_COPY.pillarLabel)}
                  </p>
                  <h3 className="mb-1 break-words text-lg font-bold text-[#002B7F] sm:text-2xl">
                    {safeT('landing_dual_ledger.card_degree', CARD_COPY.degreeTitle)}
                  </h3>
                  <p className="break-words text-sm leading-relaxed text-[#001E5A]">
                    {safeT('landing_dual_ledger.card_degree_detail', CARD_COPY.degreeDetail)}
                  </p>
                  <div className="mt-4 flex min-w-0 flex-wrap gap-2">
                    <span className="max-w-full whitespace-normal break-words rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800">
                      {safeT('landing_dual_ledger.card_badge1', CARD_COPY.badge1)}
                    </span>
                    <span className="max-w-full whitespace-normal break-words rounded-full bg-violet-100 px-3 py-1 text-xs font-bold text-violet-800">
                      {safeT('landing_dual_ledger.card_badge2', CARD_COPY.badge2)}
                    </span>
                    <span className="max-w-full whitespace-normal break-words rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-900">
                      {safeT('landing_dual_ledger.card_badge3', CARD_COPY.badge3)}
                    </span>
                  </div>
                </div>

                <div className="mb-6 grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="min-w-0 max-w-full">
                    <p className="mb-1 break-words text-xs font-semibold uppercase tracking-wider text-[#001E5A]/60">
                      {safeT('landing_dual_ledger.card_individual_label', CARD_COPY.individualLabel)}
                    </p>
                    <p className="break-words text-sm font-semibold text-[#002B7F]">
                      {safeT('landing_dual_ledger.card_individual_value', CARD_COPY.individualTitle)}
                    </p>
                    <p className="break-words text-xs leading-relaxed text-[#001E5A]/75">
                      {safeT('landing_dual_ledger.card_individual_body', CARD_COPY.individualBody)}
                    </p>
                  </div>
                  <div className="min-w-0 max-w-full">
                    <p className="mb-1 break-words text-xs font-semibold uppercase tracking-wider text-[#001E5A]/60">
                      {safeT('landing_dual_ledger.card_pillar_col_label', CARD_COPY.pillarColLabel)}
                    </p>
                    <span className="pillar-badge max-w-full whitespace-normal break-words">
                      {safeT('landing_dual_ledger.card_pillar_col_value', CARD_COPY.pillarColValue)}
                    </span>
                  </div>
                </div>

                <div
                  className="w-full min-w-0 max-w-full overflow-hidden rounded-xl border-2 border-archive-gold/40 p-4"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(201,168,76,0.08), rgba(16,185,129,0.06), rgba(139,92,246,0.08))',
                  }}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-archive-gold">
                      <ShieldCheck size={18} style={{ color: '#0F2041' }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="mb-0.5 break-words text-xs font-semibold uppercase tracking-wider text-[#001E5A]/60">
                        {safeT('landing_dual_ledger.card_verified_by_label', CARD_COPY.verifiedLabel)}
                      </p>
                      <p className="break-words font-bold text-[#002B7F]">
                        {safeT('landing_dual_ledger.card_verified_by_name', CARD_COPY.verifiedTitle)}
                      </p>
                      <p className="mt-0.5 break-words text-xs leading-relaxed text-[#001E5A]/75">
                        {safeT('landing_dual_ledger.card_verified_by_detail', CARD_COPY.verifiedBody)}
                      </p>
                      <p className="mt-1 break-all font-mono text-xs text-[#001E5A]/60">
                        VRFY-EPI-20240315-0891-A4F2
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <Link2 size={14} className="shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1 break-all font-mono text-xs text-slate-500">
                    {safeT('landing_dual_ledger.card_copy_url', CARD_COPY.copyUrl)}
                  </span>
                  <button
                    type="button"
                    className="ml-auto shrink-0 text-xs font-semibold text-trust-blue hover:underline"
                  >
                    {safeT('landing_dual_ledger.card_copy_button', CARD_COPY.copyButton)}
                  </button>
                </div>

                <div className="mt-4 grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-3">
                  {COLOR_LABEL_KEYS.map(({ color, labelKey, fallback }) => (
                    <div
                      key={labelKey}
                      className="min-w-0 max-w-full rounded-xl border border-slate-100 p-3 text-center text-xs font-bold text-slate-700"
                    >
                      <div
                        className="mx-auto mb-1 h-3 w-10 max-w-full rounded-full"
                        style={{ background: color }}
                      />
                      <span className="block break-words whitespace-normal">
                        {safeT(labelKey, fallback)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="min-w-0 space-y-8">
            <div>
              <div className="mb-4 inline-flex max-w-full items-center gap-2 rounded-full border border-trust-blue/20 bg-trust-blue/5 px-4 py-2">
                <Eye size={14} className="shrink-0 text-trust-blue" />
                <span className="break-words text-sm font-semibold text-trust-blue">
                  {safeT('landing_dual_ledger.title', 'Individual Ledger')}
                </span>
              </div>
              <h2 className="mb-4 break-words text-3xl font-bold tracking-tight text-[#002B7F] sm:text-4xl lg:text-5xl">
                {safeT('landing_dual_ledger.subtitle', 'Your vault for records and proof')}
              </h2>
              <p className="break-words text-base leading-relaxed text-[#001E5A] sm:text-lg">
                {safeT(
                  'landing_dual_ledger.body',
                  'Individuals preserve private records, create self-recorded stories, add evidence later, request witness confirmation, and share limited public summaries when ready.',
                )}
              </p>
              <ul className="mt-4 space-y-2 text-sm text-[#001E5A]/85">
                {LIST_ITEM_KEYS.map((key) => (
                  <li key={key} className="flex min-w-0 items-start gap-2">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-trust-blue" />
                    <span className="min-w-0 break-words">{safeT(key, '')}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4">
              {FEATURE_KEYS.map(({ icon: Icon, titleKey, descKey }) => (
                <div
                  key={titleKey}
                  className="flex min-w-0 items-start gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-trust-blue/10">
                    <span className="text-trust-blue">
                      <Icon size={18} />
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h4 className="mb-1 break-words font-semibold text-[#002B7F]">
                      {safeT(titleKey, '')}
                    </h4>
                    <p className="break-words text-sm leading-relaxed text-[#001E5A]">
                      {safeT(descKey, '')}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-archive-gold/30 bg-archive-gold-pale p-5">
              <div className="flex min-w-0 items-start gap-3">
                <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-archive-gold" />
                <p className="min-w-0 break-words text-sm font-semibold leading-relaxed text-[#001E5A]">
                  {safeT(
                    'landing_dual_ledger.disclaimer',
                    'Ertzyx proof summaries and certificates preserve structured evidence and review context.',
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
